import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import {Json} from "@supabaseTypes";
import { SaccoWithdrawal } from "@/@types/sacco.models.ts";
import {convertToAccountCurrency} from "@lib/currencyConversion/accountCurrency.ts";
import {convertLinkedSaccoAmount} from "./linkedAmount.ts";

/**
 * Service class responsible for handling Sacco withdrawals with credit functionality.
 * Extends the functionality of the BaseService to interface with the backend and execute
 * the withdrawal operations through a stored procedure.
 */
export class SaccoWithdrawalService extends BaseService {

    /**
     * Creates a Sacco withdrawal with credit for a specified user.
     *
     * @param {string} userId - The unique identifier of the user making the withdrawal.
     * @param {SaccoWithdrawalWithCreditRequest} request - The request object containing withdrawal details, such as
     * membership ID, target account, amount, currency, withdrawal date, and an optional note.
     * @return {Promise<Json>} A promise that resolves to the data returned from the withdrawal creation or throws an
     * error if the operation fails.
     */
    public async createWithCredit(userId: string, request: SaccoWithdrawalWithCreditRequest): Promise<Json> {
        try {
            const { convertedAmount } = await convertToAccountCurrency(
                userId, request.targetAccountId, request.amount, 0, request.withdrawalCurrency
            );
            const { data, error } = await supabase.rpc('create_sacco_withdrawal_with_credit', {
                p_user_id: userId,
                p_membership_id: request.membershipId,
                p_target_account_id: request.targetAccountId,
                p_amount: request.amount,
                p_withdrawal_currency: request.withdrawalCurrency,
                p_withdrawal_date: request.withdrawalDate,
                p_note: request.note ?? null,
                p_credit_amount: convertedAmount,
            });

            if (error) throw new Error(error.message || 'Failed to record withdrawal');
            if (!data) throw new Error('No data returned from withdrawal creation');
            return data;
        } catch (error) {
            console.error("Error creating Sacco deposit with debit", error);
            await this.handleError(error);
        }
    }

    /**
     * @deprecated use createWithCredit instead
     */
    public async create(userId: string, membershipId: string, request: SaccoWithdrawalCreateRequest): Promise<Json> {
        const payload = this.convertRequestToSnakeCase(request);
        try {
            const { data, error } = await supabase
                .from('sacco_withdrawals')
                .insert({ ...payload, membership_id: membershipId, user_id: userId })
                .select()
                .single();
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error creating Sacco withdrawals", error);
            await this.handleError(error);
        }
    }

    /**
     * Fetch all Sacco withdrawals for a given user and membership
     * @param {string} userId User's ID
     * @param {string} membershipId Membership ID
     */
    public async fetchAll(userId: string, membershipId: string): Promise<SaccoWithdrawal[]> {
        try {
            const { data, error } = await supabase
                .from('sacco_withdrawals')
                .select('*')
                .eq('membership_id', membershipId)
                .eq('user_id', userId)
                .order('withdrawal_date', { ascending: true });
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error fetching Sacco withdrawals", error);
            await this.handleError(error);
        }
    }

    /**
     * Updates a withdrawal. A withdrawal credited to an account has its linked transaction re-priced
     * and the target account balance adjusted in the same database transaction.
     * @param {string} userId User's ID
     * @param {SaccoWithdrawalUpdateRequest} request Update request
     */
    public async update(userId: string, request: SaccoWithdrawalUpdateRequest): Promise<SaccoWithdrawal> {
        try {
            const creditAmount = await convertLinkedSaccoAmount(
                userId, 'sacco_withdrawals', request.id, request.amount
            );
            const { data, error } = await supabase.rpc('update_sacco_withdrawal_with_credit', {
                p_withdrawal_id: request.id,
                p_user_id: userId,
                p_amount: request.amount,
                p_withdrawal_date: request.withdrawalDate,
                p_note: request.note ?? null,
                p_credit_amount: creditAmount,
            });
            if (error) throw error;
            return data
        } catch (error) {
            console.error("Error updating sacco withdrawal ", error);
            await this.handleError(error);
        }
    }

    /**
     * Deletes a withdrawal. A withdrawal credited to an account is deleted together with its
     * transaction, and the target account's balance is restored.
     * @param {string} id Sacco withdrawal ID
     * @param {string} userId User's ID
     */
    public async delete(id: string, userId: string): Promise<void> {
        try {
            const { data: withdrawal, error: fetchError } = await supabase
                .from('sacco_withdrawals')
                .select('transaction_id')
                .eq('id', id)
                .single();
            if (fetchError) throw fetchError;

            if (withdrawal?.transaction_id) {
                const { error } = await supabase.rpc('delete_transaction_with_balance', {
                    p_transaction_id: withdrawal.transaction_id,
                    p_user_id: userId,
                });
                if (error) throw error;
                return;
            }

            const { error } = await supabase.from('sacco_withdrawals').delete().eq('id', id);
            if (error) throw error;
        } catch (error) {
            console.error("Error deleting sacco withdrawal ", error);
            await this.handleError(error);
        }
    }
}

export default new SaccoWithdrawalService();
